import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  FilePlus2,
  FileUp,
  Copy,
  Layers,
  Barcode,
  Globe,
  Network,
  AlertCircle,
  Building2,
  Ship,
  FilePen,
  FileCheck2,
  type LucideIcon,
} from "lucide-react";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { Button, PageContainer, PageHeader } from "@/components/ui";

/**
 * Menú de servicios del sector Telecomunicaciones (diseño funcional v7,
 * mockup 1_menu_servicios_PROPUESTA.html). Cada tarjeta abre el flujo del
 * trámite: "Nuevo Certificado" va al Form Maestro; el resto pasa por la
 * pantalla de Selección de Certificado vigente y su pantalla dedicada.
 *
 * Fase 1: sólo "Copia Fiel" está activa (piloto del patrón). El resto de los
 * trámites se muestran como "Próximamente" hasta que se repliquen.
 */

type Estado = "activo" | "construccion";

interface ServicioCard {
  codigo: string;
  nombre: string;
  icon: LucideIcon;
  estado: Estado;
  to?: string;
  /** Etiqueta del catálogo (#) para referencia. */
  tag?: string;
}

interface Seccion {
  titulo: string;
  subtitulo: string;
  cards: ServicioCard[];
}

const SECCIONES: Seccion[] = [
  {
    titulo: "Certificado",
    subtitulo: "Trámites principales sobre el certificado",
    cards: [
      {
        codigo: "CERTIFICADO_UNICO",
        nombre: "Nuevo Certificado",
        icon: FilePlus2,
        estado: "activo",
        to: "/sector/telecom/solicitudes/nueva",
      },
      {
        codigo: "REEXPEDICION_PC",
        nombre: "Reexpedición de Certificado (Pruebas Complementarias)",
        icon: FileUp,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=reexpedicion",
        tag: "#13",
      },
      {
        codigo: "COPIA_FIEL",
        nombre: "Copia Fiel de un Certificado",
        icon: Copy,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=copia-fiel",
        tag: "#19",
      },
    ],
  },
  {
    titulo: "Ampliación / Reducción",
    subtitulo: "Modificaciones sobre un certificado vigente",
    cards: [
      {
        codigo: "AMPLIACION_MODELOS",
        nombre: "Modelos o Productos sobre un Certificado Vigente",
        icon: Layers,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=ampliacion-modelos",
        tag: "#9",
      },
      {
        codigo: "AMPLIACION_FRACCION",
        nombre: "Fracción Arancelaria",
        icon: Barcode,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=ampliacion-fraccion",
        tag: "#20",
      },
      {
        codigo: "AMPLIACION_PAISES",
        nombre: "Países",
        icon: Globe,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=ampliacion-paises",
        tag: "#22",
      },
      {
        codigo: "AMPLIACION_FILIALES",
        nombre: "De Filiales, Subsidiarias y/o Importadores",
        icon: Network,
        estado: "activo",
        to: "/telecom/servicios/seleccionar-certificado?s=ampliacion-filiales",
        tag: "#13",
      },
    ],
  },
  {
    titulo: "Cartas y Anexos",
    subtitulo: "Cartas de soporte para trámites aduanales y anexos",
    cards: [
      {
        codigo: "CARTA_ADUANA",
        nombre: "Carta Aduana",
        icon: Ship,
        estado: "activo",
        to: "/telecom/servicios/carta-aduana",
        tag: "#15",
      },
      {
        codigo: "CORRECCION_CARTA_ADUANA",
        nombre: "Corrección de Carta Aduana",
        icon: FilePen,
        estado: "activo",
        to: "/telecom/servicios/correccion-carta-aduana",
        tag: "#30",
      },
      {
        codigo: "OPINION_TECNICA",
        nombre: "Carta de Opinión Técnica",
        icon: FileCheck2,
        estado: "activo",
        to: "/telecom/servicios/opinion-tecnica",
        tag: "#17",
      },
    ],
  },
];

export function TelecomServiciosMenuPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [sinCtx, setSinCtx] = useState(false);
  const [confirmCtx, setConfirmCtx] = useState<{ to: string; nombre: string } | null>(null);

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  // Antes de iniciar cualquier trámite el cliente debe haber elegido el
  // contexto de actuación en el ContextSelector (sessionStorage). Los
  // internos del OC no tienen contextos y pasan directo.
  const iniciar = (to: string, nombre: string) => {
    if (access.esInterno) {
      navigate(to);
      return;
    }
    const sel = window.sessionStorage.getItem("sicen.contexto.titularId");
    if (!sel) setSinCtx(true);
    else setConfirmCtx({ to, nombre });
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        backTo="/telecom"
        backLabel="Volver a Telecom"
        title="Solicitud de servicios"
        subtitle="Elige el trámite que deseas iniciar. Los trámites sobre un certificado vigente te pedirán seleccionar primero el certificado."
      />

      {SECCIONES.map((sec) => (
        <section key={sec.titulo} className="space-y-3">
          <div>
            <h2 className="text-base font-semibold text-fg">{sec.titulo}</h2>
            <p className="text-sm text-fg-subtle">{sec.subtitulo}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sec.cards.map((c) => (
              <ServicioTarjeta key={c.codigo} card={c} onIniciar={iniciar} />
            ))}
          </div>
        </section>
      ))}

      {sinCtx && <SinContextoModal onClose={() => setSinCtx(false)} />}
      {confirmCtx && (
        <ConfirmarContextoModal
          tramiteNombre={confirmCtx.nombre}
          titularRazonSocial={access.titularRazonSocial ?? ""}
          onCancel={() => setConfirmCtx(null)}
          onConfirm={() => {
            const to = confirmCtx.to;
            setConfirmCtx(null);
            navigate(to);
          }}
        />
      )}
    </PageContainer>
  );
}

function ServicioTarjeta({
  card,
  onIniciar,
}: {
  card: ServicioCard;
  onIniciar: (to: string, nombre: string) => void;
}) {
  const Icon = card.icon;
  const inner = (
    <>
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary-tint text-primary-strong transition group-hover:bg-primary group-hover:text-white">
        <Icon className="h-6 w-6" />
      </span>
      <div className="mt-3 text-sm font-semibold text-fg">{card.nombre}</div>
      {card.estado === "construccion" && (
        <span className="absolute right-3 top-3 rounded-full bg-estado-warn-bg px-2 py-0.5 text-[10px] font-semibold text-estado-warn">
          Próximamente
        </span>
      )}
    </>
  );

  if (card.estado === "activo" && card.to) {
    return (
      <button
        type="button"
        onClick={() => onIniciar(card.to!, card.nombre)}
        className="group relative flex flex-col items-center rounded-xl border border-edge/70 bg-surface p-5 text-center shadow-card transition hover:-translate-y-0.5 hover:border-primary-strong/40 hover:shadow-card-hover"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="relative flex cursor-not-allowed flex-col items-center rounded-xl border border-dashed border-edge bg-surface-2/60 p-5 text-center opacity-70">
      {inner}
    </div>
  );
}

function SinContextoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <AlertCircle className="h-5 w-5 text-estado-warn" />
          <h2 className="text-base font-semibold text-fg">Selecciona una empresa</h2>
        </div>
        <div className="px-5 py-4 text-sm text-fg-muted">
          <p>
            Antes de iniciar un trámite necesitas elegir la empresa para la que vas a
            trabajar.
          </p>
          <p className="mt-2 text-xs text-fg-muted">
            Usa el selector de empresa en la barra superior (junto a tu nombre) y vuelve
            a intentarlo.
          </p>
        </div>
        <div className="flex justify-end border-t bg-surface-2 px-5 py-3">
          <Button size="sm" onClick={onClose}>Entendido</Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmarContextoModal({
  tramiteNombre,
  titularRazonSocial,
  onCancel,
  onConfirm,
}: {
  tramiteNombre: string;
  titularRazonSocial: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <Building2 className="h-5 w-5 text-primary-strong" />
          <h2 className="text-base font-semibold text-fg">{tramiteNombre}</h2>
        </div>
        <div className="space-y-2 px-5 py-4 text-sm text-fg-muted">
          <p>Estás a punto de iniciar el trámite para:</p>
          <div className="rounded border border-estado-info/25 bg-estado-info-bg px-3 py-2 font-medium text-estado-info">
            {titularRazonSocial || "—"}
          </div>
          <p className="text-xs text-fg-muted">
            Si necesitas trabajar con otra empresa, cancela y cámbiala en el selector de
            empresa de la barra superior.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <Button size="sm" variant="secondary" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" onClick={onConfirm}>Continuar</Button>
        </div>
      </div>
    </div>
  );
}
